/**
 * Auto-subscribes the WebSocket connection to the current farm based on URL.
 * When the user navigates to /farms/:farmId/*, only device updates for
 * that farm are delivered. When leaving a farm page, the subscription
 * is cleared so the user receives all updates again.
 */

import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { consolidatedWebSocketManager } from '@/services/websocket/consolidatedManager';

export function useWebSocketFarmSubscription() {
  const { farmId } = useParams<{ farmId?: string }>();

  useEffect(() => {
    if (!farmId) {
      // Not on a farm page — clear subscription to receive everything
      consolidatedWebSocketManager.unsubscribeFarm();
      return;
    }

    // Only subscribe once the WS is connected; retry shortly if it is still
    // initializing.
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (consolidatedWebSocketManager.isConnected()) {
      consolidatedWebSocketManager.subscribeFarm(farmId);
    } else {
      timer = setTimeout(() => {
        if (consolidatedWebSocketManager.isConnected()) {
          consolidatedWebSocketManager.subscribeFarm(farmId);
        }
      }, 2000);
    }

    return () => {
      // Both paths must unsubscribe on leaving the farm page. The deferred path
      // used to return only a clearTimeout, so a subscription made 2s after mount
      // outlived the page: a farm-wide filter then stayed on the connection and
      // silently dropped every broadcast for OTHER farms — which is exactly what
      // breaks the fleet-wide admin views.
      if (timer) clearTimeout(timer);
      consolidatedWebSocketManager.unsubscribeFarm();
    };
  }, [farmId]);
}
